import { useLayoutEffect, useState, type RefObject } from "react";
import { resolveMultiSelectRailHostRowId } from "./multiSelectRailAnchor";

export function useMultiSelectRailHostRowId(
  canvasRootRef: RefObject<HTMLElement | null>,
  selectedRowIds: readonly string[],
  primaryRowId: string | null,
): string | null {
  const [hostRowId, setHostRowId] = useState<string | null>(null);
  const selectionKey = selectedRowIds.join("\u0001");

  useLayoutEffect(() => {
    if (selectedRowIds.length <= 1) {
      setHostRowId(null);
      return;
    }

    const canvasRoot = canvasRootRef.current;
    if (!(canvasRoot instanceof HTMLElement)) {
      return;
    }

    const root = canvasRoot;

    function measure() {
      const nextHostId = resolveMultiSelectRailHostRowId(selectedRowIds, root);
      setHostRowId(previous => (previous === nextHostId ? previous : nextHostId));
    }

    measure();
    root.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      root.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [canvasRootRef, selectionKey, selectedRowIds]);

  if (selectedRowIds.length <= 1) {
    return null;
  }

  return (
    hostRowId ??
    (primaryRowId !== null && selectedRowIds.includes(primaryRowId)
      ? primaryRowId
      : selectedRowIds[0] ?? null)
  );
}
